import React, { useEffect, useState } from "react";
import { Droplets } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { dms, inr, niceDate, statusPill } from "./api";
import { ReturnRequestButton } from "./ReturnComponents";
import { ReturnDetailDialog } from "./ReturnDetail";
import { PageHeader, EmptyState, StockLedgerSection } from "./OwnerPages";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShoppingCart, Package, Warehouse, TrendingUp, ScrollText, CheckCircle2, ChevronRight, Plus, Minus, Receipt, ClipboardList, Truck, Paperclip, Trash2, Printer, Edit, Calendar, Wallet, AlertTriangle, Users, PackageX, Store, BarChart3, RotateCcw } from "lucide-react";

function RoleSaleLitersPage({ fetcher, title, subtitle, showSalesperson, showDistributor }) {
  const _n = new Date();
  const _pad = (x) => String(x).padStart(2, "0");
  const _ms = `${_n.getFullYear()}-${_pad(_n.getMonth() + 1)}-01`;
  const _me = `${_n.getFullYear()}-${_pad(_n.getMonth() + 1)}-${_pad(new Date(_n.getFullYear(), _n.getMonth() + 1, 0).getDate())}`;
  const base = { date_from: _ms, date_to: _me, distributor_id: "", salesperson_id: "", category: "", status: "sold" };
  const [f, setF] = useState(base);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = (flt) => {
    setLoading(true);
    const params = {};
    Object.keys(flt).forEach(k => { if (flt[k]) params[k] = flt[k]; });
    fetcher(params).then(setData).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  };
  useEffect(() => { load(base); /* eslint-disable-next-line */ }, []);
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));
  const sel = "h-10 rounded-md border border-slate-200 bg-white px-3 text-sm";
  const fmt = (x) => Number(x || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
  const rows = data?.rows || [];
  const opts = data?.filter_options || {};
  const byD = data?.by_distributor || [];
  return (
    <div>
      <PageHeader title={title} subtitle={subtitle} />
      <Card className="p-3 mb-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <div className="text-[11px] text-slate-500 mb-1">From</div>
            <input type="date" className={sel} value={f.date_from} onChange={e => set("date_from", e.target.value)} />
          </div>
          <div>
            <div className="text-[11px] text-slate-500 mb-1">To</div>
            <input type="date" className={sel} value={f.date_to} onChange={e => set("date_to", e.target.value)} />
          </div>
          {showDistributor && (
            <select className={sel} value={f.distributor_id} onChange={e => set("distributor_id", e.target.value)}>
              <option value="">All distributors</option>
              {(opts.distributors || []).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          )}
          {showSalesperson && (
            <select className={sel} value={f.salesperson_id} onChange={e => set("salesperson_id", e.target.value)}>
              <option value="">All salespersons</option>
              {(opts.salespersons || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
          <select className={sel} value={f.category} onChange={e => set("category", e.target.value)}>
            <option value="">All categories</option>
            {(opts.categories || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select className={sel} value={f.status} onChange={e => set("status", e.target.value)}>
            <option value="sold">Sold (Invoiced + Dispatched)</option>
            <option value="dispatched">Dispatched</option>
            <option value="invoiced">Invoiced</option>
            <option value="pending">Pending</option>
            <option value="all">All (incl. Pending)</option>
          </select>
          <Button onClick={() => load(f)}>Apply</Button>
          <Button variant="outline" onClick={() => { setF(base); load(base); }}>Reset</Button>
        </div>
      </Card>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
        <Card className="p-4"><div className="text-xs text-slate-500 uppercase tracking-wider">Total Liters</div><div className="mt-1 text-2xl font-bold text-slate-900">{fmt(data?.total_liters)} L</div></Card>
        <Card className="p-4"><div className="text-xs text-slate-500 uppercase tracking-wider">Total Pieces</div><div className="mt-1 text-2xl font-bold text-slate-900">{fmt(data?.total_pieces)}</div></Card>
        <Card className="p-4"><div className="text-xs text-slate-500 uppercase tracking-wider">Orders</div><div className="mt-1 text-2xl font-bold text-slate-900">{data?.order_count ?? 0}</div></Card>
      </div>
      {showDistributor && byD.length > 0 && (
        <Card className="overflow-x-auto mb-3">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Distributor</TableHead>
              <TableHead className="text-right">Orders</TableHead>
              <TableHead className="text-right">Pieces</TableHead>
              <TableHead className="text-right">Liters</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {byD.map(d => (
                <TableRow key={d.distributor_id}>
                  <TableCell className="font-medium">{d.distributor_name}</TableCell>
                  <TableCell className="text-right">{d.orders}</TableCell>
                  <TableCell className="text-right">{fmt(d.pieces)}</TableCell>
                  <TableCell className="text-right font-semibold">{fmt(d.liters)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>SKU</TableHead><TableHead>Item</TableHead><TableHead>Category</TableHead><TableHead>Pack</TableHead>
            <TableHead className="text-right">Pieces</TableHead><TableHead className="text-right">Liters</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-400">Loading...</TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-400">No sale found for these filters</TableCell></TableRow>}
            {rows.map((r, i) => (
              <TableRow key={i}>
                <TableCell className="font-mono text-xs">{r.sku_code || "-"}</TableCell>
                <TableCell className="font-medium">{r.product_name}</TableCell>
                <TableCell className="text-xs">{r.category}</TableCell>
                <TableCell className="text-xs">{r.pack_size || "-"}</TableCell>
                <TableCell className="text-right">{fmt(r.pieces)}</TableCell>
                <TableCell className="text-right font-semibold">{fmt(r.liters)}</TableCell>
              </TableRow>
            ))}
            {!loading && rows.length > 0 && (
              <TableRow className="bg-slate-50 font-bold">
                <TableCell colSpan={4}>Total</TableCell>
                <TableCell className="text-right">{fmt(data?.total_pieces)}</TableCell>
                <TableCell className="text-right">{fmt(data?.total_liters)}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

export function TlSaleLitersPage() {
  return <RoleSaleLitersPage fetcher={(p) => dms.tlSaleLiters(p)} title="Sale in Liters" subtitle="Liters sold across your distributors and team" showDistributor showSalesperson />;
}
export function RmSaleLitersPage() {
  return <RoleSaleLitersPage fetcher={(p) => dms.rmSaleLiters(p)} title="Sale in Liters" subtitle="Liters sold across your region" showDistributor showSalesperson />;
}
export function SpSaleLitersPage() {
  return <RoleSaleLitersPage fetcher={(p) => dms.spSaleLiters(p)} title="Sale in Liters" subtitle="Liters sold on your orders" showDistributor />;
}
